// Copyright (c) Docugami, Inc. All rights reserved.

import { DirectionalHint } from "@fluentui/react";
import { ClauseType } from "components/assisted_authoring/ClauseSuggestionsList";
import { ICommand, ICommandButton, IConfirmationCommandButton } from "components/cards/CommandButton"
import { homeGray, clauseSelectedGreen, primaryTheme, subheadGrey, themeRed } from "components/shell/presentation/AppStyles"
import { useEffect, useMemo, useRef } from "react";
import { useRemoveClauseLabelMutation, useRemoveClauseMutation, useHideClausesMutation, useUnhideClausesMutation } from "store/assistedauthinfo/assistedAuthoringSlice";
import { useAaDgWordCanvasProxy } from "./aaDgWordCanvasProxy"
import { dgIconNames } from "./dgIcons"
import { useSaveClauseCommand } from "./SaveClauseCommand";

export const emptyCommand: ICommandButton = (
    {
        icon: {
            name: '',
            height: 0,
            width: 0
        },
        label: '',
        canExecute: false
    }
)

interface IClauseCommand extends ICommand {
    tooltip?: string;
}

export function useInsertClauseCommand(documentId: string, dgmlContent?: string): IClauseCommand {
    const dgWordCanvasProxy = useAaDgWordCanvasProxy(documentId);
    const canExecute = useMemo(() => !!dgmlContent && !!dgWordCanvasProxy?.initialized, [dgmlContent, dgWordCanvasProxy])

    return {
        canExecute: () => canExecute,
        execute: async () => { await dgWordCanvasProxy?.insertContentAtSelection(dgmlContent!) },
        tooltip: !canExecute ? '' : 'Insert this clause into your document at the current caret position'
    }
}

export function useAddVariantCommand(documentId: string, label: string, onComplete?: (id: string) => void): IClauseCommand {
    const { setLabel, canSaveClause, saveClause, data } = useSaveClauseCommand(documentId);
    useEffect(() => setLabel(label), [setLabel, label]);

    const completeCallback = useRef(onComplete);

    useEffect(() => {
        if (!!data?.id && !!completeCallback.current) {
            // Fire the onComplete callback once when data is loaded
            completeCallback.current(data.id);
            completeCallback.current = undefined;
        }
    }, [data])
    
    return {
        canExecute: () => canSaveClause,
        execute: saveClause,
        tooltip: canSaveClause ? 'Add highlighted content from document as a variant to this item' : 'Select content from your document to add as a variant to this item'
    }
}

interface RemoveClauseCommands {
    removeClause: IClauseCommand;
    removeClauseLabel: IClauseCommand;
}

export function useRemoveClauseCommands(clusterId: string, clauseItemId: string, variantId?: string): RemoveClauseCommands {
    const [ triggerRemoveClause ] = useRemoveClauseMutation();
    const [ triggerRemoveClauseLabel ] = useRemoveClauseLabelMutation();

    return {
        removeClause:  {
            canExecute: () => !!variantId,
            execute: async () => { await triggerRemoveClause({docSetId: clusterId, clauseId: variantId! }) },
            tooltip: !!variantId ? 'Remove this variant from this item' : undefined
        },
        removeClauseLabel:  {
            canExecute: () => !!clauseItemId,
            execute: async () => { await triggerRemoveClauseLabel({docSetId: clusterId, clauseLabelId: clauseItemId }) },
            tooltip: !!clauseItemId ? 'Removing this item will remove all associated variants from your clause library' : undefined
        }
    }
}

export function useHideClauseCommand(documentId: string, clauseId: string, type: ClauseType, isHidden: boolean): ICommand {
    const [ triggerHideClause ] = useHideClausesMutation();
    const [ triggerUnhideClause ]= useUnhideClausesMutation();

    const isSuggestionId = type === ClauseType.Insert;

    const request = {
        documentId: documentId,
        spanIds: isSuggestionId ? [] : [clauseId],
        suggestionIds: isSuggestionId ? [clauseId] : []
    }

    return {
        canExecute: () => !!clauseId,
        execute: async () => { isHidden ? triggerUnhideClause(request) : triggerHideClause(request) }
    }
}

// Clause command button helpers
export function getAddClauseToLibraryCommand(addClause: IClauseCommand): ICommandButton {
    return ({
        icon: {
            name: dgIconNames.AssistedAuthoringClauseLibrary,
            height: 21,
            width: 16,
            defaultColor: homeGray
        },
        label: 'ADD TO LIBRARY',
        showLabel: true,
        labelColor: homeGray,
        backgroundColor: clauseSelectedGreen,
        canExecute: addClause.canExecute(),
        onClick: addClause.execute,
        tooltipProps: {
            content: addClause.tooltip,
            directionalHint: DirectionalHint.bottomCenter
        }
    })
}

export function getInsertClauseButton(onInsert: IClauseCommand): ICommandButton {
    return {
        icon: {
            name: dgIconNames.AssistedAuthoringPaste,
            height: 19,
            width: 15,
            defaultColor: homeGray
        },
        label: 'INSERT',
        showLabel: true,
        labelColor: homeGray,
        backgroundColor: clauseSelectedGreen,
        canExecute: onInsert.canExecute(),
        onClick: onInsert.execute,
        tooltipProps: {
            content: onInsert.tooltip,
            directionalHint: DirectionalHint.bottomCenter
        }
    }
}

export function getAddVariantButton(addVariant: IClauseCommand): ICommandButton {
    return {
        icon: {
            name: dgIconNames.DgPlusBox,
            height: 19,
            width: 15,
            defaultColor: homeGray
        },
        label: 'ADD VARIANT',
        showLabel: true,
        labelColor: homeGray,
        backgroundColor: subheadGrey,
        canExecute: addVariant.canExecute(),
        onClick: addVariant.execute,
        tooltipProps: {
            content: addVariant.tooltip,
            directionalHint: DirectionalHint.bottomCenter
        }
    }
}

export function getRemoveClauseButton(onRemove: IClauseCommand): IConfirmationCommandButton {
    return {
        icon: {
            name: dgIconNames.DgClearIcon,
            height: 19,
            width: 14,
            defaultColor: homeGray
        },
        label: 'REMOVE VARIANT',
        showLabel: true,
        backgroundColor: subheadGrey,
        canExecute: onRemove.canExecute(),
        onClick: onRemove.execute,
        tooltipProps: {
            content: onRemove.tooltip,
            directionalHint: DirectionalHint.bottomCenter
        },
        confirmationLabel: 'REMOVE VARIANT ? ',
        confirmationBackgroundColor: themeRed,
        confirmationColor: primaryTheme.palette.white
    }
}

export function getRemoveClauseFromLibraryButton(onRemove: IClauseCommand): IConfirmationCommandButton {
    return {
        icon: {
            name: dgIconNames.AssistedAuthoringClauseLibraryRemove,
            height: 18,
            width: 18,
            defaultColor: homeGray
        },
        label: 'REMOVE FROM LIBRARY',
        showLabel: false,
        backgroundColor: subheadGrey,
        canExecute: onRemove.canExecute(),
        onClick: onRemove.execute,
        tooltipProps: {
            content: onRemove.tooltip,
            directionalHint: DirectionalHint.bottomCenter
        },
        confirmationLabel: 'REMOVE ?',
        confirmationBackgroundColor: themeRed,
        confirmationColor: primaryTheme.palette.white
    }
}

export function getHideClauseButton(onHide: ICommand, isHidden: boolean): ICommandButton {
    return {
        icon: {
            name: dgIconNames.DgHide,
            height: 18,
            width: 18,
            defaultColor: homeGray
        },
        label: isHidden ? 'UNHIDE' : 'HIDE',
        showLabel: true,
        backgroundColor: subheadGrey,
        canExecute: onHide.canExecute(),
        onClick: onHide.execute
    }
}